import { type IntlShape, type MessageDescriptor } from "react-intl";

import { messages } from "../components/messages.generated";

/** The BFF's structured rejection on a failed PUSH_LENS row (structural, to fit the mutation's Pick type). */
export interface WebLensRejection {
    reason: string;
    key?: string | null;
    data?: string | null;
    omittedCount: number;
    findings: readonly { ruleId: string; file: string; line: number; column: number; occurrences: number }[];
}

export interface WebLensRejectionDescription {
    detail: string;
    issues?: string[];
}

export const SECURITY_REVIEW_KEY = "web_app_security_review_failure";

// Lens Central's per-key sentences (rejection-reasons/en_US.json), phrased for "send again".
// dataName is the placeholder a key's data fills.
const KEY_MESSAGES: Record<string, { message: MessageDescriptor; dataName?: string }> = {
    web_app_not_zip: { message: messages.rejectionNotZip },
    web_app_compressed_over_limit: { message: messages.rejectionCompressedOverLimit, dataName: "limit" },
    web_app_inflated_over_limit: { message: messages.rejectionInflatedOverLimit, dataName: "limit" },
    web_app_entry_count_over_limit: { message: messages.rejectionEntryCountOverLimit, dataName: "limit" },
    web_app_unsafe_entry: { message: messages.rejectionReasonUnsafeArchiveEntry },
    web_app_no_built_output: { message: messages.rejectionNoBuiltOutput, dataName: "dirs" },
    web_app_source_project: { message: messages.rejectionSourceProject },
    web_app_source_files: { message: messages.rejectionSourceFiles, dataName: "count" },
    web_app_dev_entry: { message: messages.rejectionDevEntry },
    web_app_missing_refs: { message: messages.rejectionMissingRefs, dataName: "refs" },
    web_app_bundle_over_limit: { message: messages.rejectionBundleOverLimit, dataName: "limit" },
    // lens-bundle sends this when its scan reaches a shared analysis limit; Lens Central shows the
    // WL-SCAN-001 sentence for it, so the rule's message is the same text.
    web_app_scan_incomplete: { message: messages.rejectionRuleScan001 },
};

// Reason-level text when there is no usable key.
const REASON_MESSAGES: Record<string, MessageDescriptor> = {
    WEB_APP_CONVERSION_FAILURE: messages.rejectionReasonConversionFailure,
    WEB_APP_PACKAGE_UNREADABLE: messages.rejectionReasonPackageUnreadable,
    WEB_APP_PACKAGE_TOO_LARGE: messages.rejectionReasonPackageTooLarge,
    WEB_APP_UNSAFE_ARCHIVE_ENTRY: messages.rejectionReasonUnsafeArchiveEntry,
    WEB_APP_BUNDLE_TOO_LARGE: messages.rejectionReasonBundleTooLarge,
};

// Every rule that can block: HIGH/CRITICAL plus WL-SCAN-001.
const RULE_MESSAGES: Record<string, MessageDescriptor> = {
    "WL-RTC-001": messages.rejectionRuleRtc001,
    "WL-RTC-002": messages.rejectionRuleRtc002,
    "WL-NET-001": messages.rejectionRuleNet001,
    "WL-NET-003": messages.rejectionRuleNet003,
    "WL-NET-005": messages.rejectionRuleNet005,
    "WL-REALM-002": messages.rejectionRuleRealm002,
    "WL-DEV-001": messages.rejectionRuleDev001,
    "WL-DEV-002": messages.rejectionRuleDev002,
    "WL-DYN-003": messages.rejectionRuleDyn003,
    "WL-FS-001": messages.rejectionRuleFs001,
    "WL-FS-002": messages.rejectionRuleFs002,
    "WL-SCAN-001": messages.rejectionRuleScan001,
};

/**
 * Localized detail for a rejection, plus one issue per rule for a security block. Undefined when
 * unrecognized, so the caller keeps the BFF's verbatim text.
 */
export const describeWebLensRejection = (
    intl: IntlShape,
    rejection: WebLensRejection,
): WebLensRejectionDescription | undefined => {
    if (rejection.key === SECURITY_REVIEW_KEY) {
        if (rejection.findings.length === 0) {
            return { detail: intl.formatMessage(messages.rejectionSecurityFailedNoDetails) };
        }
        const issues = rejection.findings.map((finding) =>
            intl.formatMessage(messages.rejectionSecurityFinding, {
                rule: intl.formatMessage(RULE_MESSAGES[finding.ruleId] ?? messages.rejectionRuleUnknown, {
                    ruleId: finding.ruleId,
                }),
                file: finding.file,
                line: finding.line,
                column: finding.column,
                occurrences: finding.occurrences,
            }),
        );
        if (rejection.omittedCount > 0) {
            issues.push(intl.formatMessage(messages.rejectionSecurityMore, { count: rejection.omittedCount }));
        }
        return { detail: intl.formatMessage(messages.rejectionSecurityFailed), issues };
    }

    const keyed = rejection.key ? KEY_MESSAGES[rejection.key] : undefined;
    // A data-bearing sentence needs its data; otherwise fall back to the reason text.
    if (keyed && (!keyed.dataName || rejection.data)) {
        return {
            detail: intl.formatMessage(
                keyed.message,
                keyed.dataName ? { [keyed.dataName]: rejection.data } : undefined,
            ),
        };
    }

    const reasonMessage = REASON_MESSAGES[rejection.reason];
    return reasonMessage ? { detail: intl.formatMessage(reasonMessage) } : undefined;
};
